import React from 'react';
import cn from 'classnames';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { Stack } from '@lyearn/ui';
import stopPropagation from '@/consts/stopPropogation';

import useCategoryActions from '../../hooks/useCategoryActions';
import useCategoryPermissions from '../../hooks/useCategoryPermissions';
import { CategoryActionsEnum, CategoryLayoutView, CategoryType } from '../../types';
import { getInitialExpandedState } from '../../utils';
import type { DraggingItemType, OnDragContentArgs } from '../CategoryList/types';
import CategoryTree from '../CategoryTree';
import CategoryActionMenu from './ActionMenu';
import CategoryDropOver from './CategoryDropOver';
import CategoryExpandButton from './ExpandButton';
import CategoryFooter from './Footer';
import CategoryName from './Name';

interface CategoryItemProps {
  category: CategoryType;
  layoutView: CategoryLayoutView;
  level: number;
  actions: ReturnType<typeof useCategoryActions>['actions'];
  state: ReturnType<typeof useCategoryActions>['state'];
  draggingItem: React.MutableRefObject<DraggingItemType | undefined>;
  parentCategory?: CategoryType;
  index: number;
  onDrop: (args: OnDragContentArgs) => void;
  isMoving?: boolean;
  hierarchyPath: string[];
}

const CategoryItem = ({
  category,
  layoutView,
  level,
  actions,
  state,
  draggingItem,
  index,
  onDrop,
  parentCategory,
  isMoving,
  hierarchyPath,
}: CategoryItemProps) => {
  //  will display current category details and actions, render child categories
  const hasChildren = Boolean(category.childCategories?.length);
  const { isSelected, toggleSelection, onAddCategory, onAction } = actions;
  const isChecked = isSelected(category.id);
  const { editingCategory, actionMenuItems, fetchingOnCreate } = state;
  const [isExpanded, { toggle, on }] = useToggle(() =>
    getInitialExpandedState({ isSelected, categoriesTree: category.childCategories }),
  );

  const isEditing = editingCategory === category.id;

  const onToggleSelection = React.useCallback(
    (event: any) => {
      stopPropagation(event);
      toggleSelection(category.id);
    },
    [category.id, toggleSelection],
  );

  const onExpandClick = useEvent((e) => {
    stopPropagation(e);
    toggle();
  });

  const onAdd = useEvent((e) => {
    stopPropagation(e);
    onAddCategory(category.id, on);
  });

  const _onAction = React.useCallback(
    (actionType: CategoryActionsEnum) => {
      switch (actionType) {
        case CategoryActionsEnum.AddSubcategory:
          onAction(actionType, category, on);
          break;
        default:
          onAction(actionType, category);
      }
    },
    [category, on, onAction],
  );

  const [isDragging, setDraggable] = React.useState(false);
  const { permissions } = useCategoryPermissions();

  return (
    <>
      <Stack
        alignItems="center"
        className="item-container group space-s-4"
        draggable={isDragging && !isMoving && layoutView === 'edit' && permissions.canEdit}
        onDragStart={() => {
          draggingItem.current = {
            currentOrder: index,
            category: category,
            parentCategory: parentCategory,
          };
        }}>
        <CategoryActionMenu
          actionMenuItems={actionMenuItems}
          className={cn({ 'active:bg-bg-transparent hover:bg-bg-transparent': isDragging })}
          layoutView={layoutView}
          onAction={_onAction}
          onMouseEnter={() => setDraggable(true)}
          onMouseLeave={() => setDraggable(false)}
        />
        <Stack
          alignItems="center"
          className="border-container h-full flex-1 cursor-pointer hover:bg-bg-hover-light"
          role="button"
          style={{ paddingInlineStart: `${(level * 20) / 10 + 1.6}rem` }}
          onClick={layoutView === 'single-select' ? onToggleSelection : toggle}>
          <CategoryExpandButton
            hasChildren={hasChildren}
            isExpanded={isExpanded}
            layoutView={layoutView}
            onClick={onExpandClick}
          />
          <CategoryDropOver
            className={cn('relative h-full w-full', layoutView === 'edit' ? 'pe-20' : 'pe-16')}
            currentCategory={category}
            disableMiddle={isDragging}
            draggingItem={draggingItem}
            hierarchyPath={hierarchyPath}
            onDropBottom={() => {
              if (isExpanded && hasChildren) {
                // this is in case when the category is open, it should add as the first child, else should add to parent
                onDrop({
                  orderInNewParent: 0,
                  newParentCategory: category,
                });
                return;
              }

              const currentOrder = draggingItem.current?.currentOrder || 0;
              const isSameParent = draggingItem.current?.parentCategory?.id === parentCategory?.id;

              // Note: using this syntax for better readability
              const orderInNewParent = (() => {
                if (!isSameParent) return index + 1;
                return currentOrder > index ? index + 1 : index; // is it is the same parent, and moving form top to bottom, we should adjust the new index accordingly
              })();

              onDrop({ orderInNewParent, newParentCategory: parentCategory });
            }}
            onDropMiddle={() => {
              onDrop({
                orderInNewParent: 0,
                newParentCategory: category,
              });
              on();
            }}
            onDropTop={() => {
              const currentOrder = draggingItem.current?.currentOrder || 0;
              const isSameParent = draggingItem.current?.parentCategory?.id === parentCategory?.id;

              // Note: using this syntax for better readability
              const orderInNewParent = (() => {
                if (!isSameParent) return index;
                return currentOrder > index ? index : index - 1; // is it is the same parent, and moving form bottom to top, we should adjust the new index accordingly
              })();

              onDrop({ orderInNewParent, newParentCategory: parentCategory });
            }}>
            <Stack alignItems="center" className="flex-1 py-16 ps-8" justifyContent="between">
              <CategoryName actions={actions} category={category} isEditing={isEditing} />
              <CategoryFooter
                contentCount={category.contentCount || 0}
                fetchingOnCreate={fetchingOnCreate}
                isChecked={isChecked}
                layoutView={layoutView}
                onAdd={onAdd}
                onToggleSelection={onToggleSelection}
              />
            </Stack>
          </CategoryDropOver>
        </Stack>
      </Stack>
      {isExpanded && hasChildren ? (
        <CategoryTree
          key={category.id}
          actions={actions}
          categoryTrees={category.childCategories}
          draggingItem={draggingItem}
          hierarchyPath={[...hierarchyPath, category.id]}
          isMoving={isMoving}
          layoutView={layoutView}
          level={level + 1}
          parentCategory={category}
          state={state}
          onDrop={onDrop}
        />
      ) : null}
    </>
  );
};

export default React.memo(CategoryItem);
